<script lang="ts">
  import { regionLookup } from '$lib/utils/lookups'
  import ResourceCard from '$lib/components/card/ResourceCard.svelte'
  import CardListWrapper from '$lib/wrappers/CardListWrapper.svelte'
  import { pageHrefs } from '$lib/constants/breadCrumbConstants'
  import type { LandingPage, ServiceAreaElementBase } from '$lib/types/ServicePageTypes'
  import { _ } from 'svelte-i18n'
  import SEO from '$lib/components/seo/SEO.svelte'
  import H1 from '$lib/components/typography/H1.svelte'
  import H4 from '$lib/components/typography/H4.svelte'
  import GeneralSectionWrapper from '$lib/wrappers/GeneralSectionWrapper.svelte'
  import { page } from '$app/stores'
  import MatOgDrikke from '$lib/components/icons/largeIcons/MatOgDrikke.svelte'
  import CtaSection from '$lib/components/cta/CtaSection.svelte'
  import Resources from '$lib/components/landingPage/Resources.svelte'
  import { CtaDoubleSection } from '$lib/components/cta'
  import LandingPageFaqWrapper from '$lib/components/article/LandingPageFaqWrapper.svelte'
  import LocationButtonMenu from '$lib/components/locationButtonMenu/LocationButtonMenu.svelte'
  import logoStore from '$lib/stores/logoStore'
  import { onDestroy } from 'svelte'

  const user = $page.data.session?.user

  export let data: LandingPage
  type Location = {
    key: string
    value: string
  }
  type Locations = {
    [key: string]: Location
  }
  export const {
    title,
    ingress,
    resources,
    seo,
    serviceAreaElements,
    relevantFaqs,
    ctaSection1,
    ctaSection2,
    ctaSectionDouble,
  } = data

  const locations = Object.keys(regionLookup).reduce((acc: Locations, key: string) => {
    return key !== 'all' && key !== 'default'
      ? {
          ...acc,
          [key]: {
            key,
            value: $_(`location.${key}`),
          },
        }
      : acc
  }, {})

  // put serviceAreaElements in a key value list by display name
  const serviceAreaElementsByKey = serviceAreaElements?.reduce(
    (acc: { [key: string]: ServiceAreaElementBase }, element: ServiceAreaElementBase) => {
      const key = element?.displayName && element.displayName.trim().length > 0 ? element.displayName : element.title
      return {
        ...acc,
        [key]: element,
      }
    },
    {},
  )
  // extract the keys from the serviceAreaElements in a sorted list ascending
  const serviceAreaElementKeys: string[] = serviceAreaElementsByKey
    ? Object.keys(serviceAreaElementsByKey).sort((a, b) => {
        return a.localeCompare(b)
      })
    : []

  const userLocation = user?.location || 'bergen'
  $: activeId = $page.url.searchParams.get('location') || userLocation || 'bergen'
  $: logoStore.set(activeId)
  let elementCount = 0
  $: elementCount = serviceAreaElements
    ? serviceAreaElements.filter((element) => element.location == activeId).length
    : 0

  onDestroy(() => {
    logoStore.clear()
  })
</script>

<SEO title={seo?.metaTitle || title} description={seo?.metaDescription || ingress} />

<GeneralSectionWrapper class="bg-primary-50 !pb-0">
  <MatOgDrikke />
  <H1 class="text-gray-800 pb-4">{title}</H1>
  {#if ingress}
    <H4 class="text-base-black pb-8">{ingress}</H4>
  {/if}
</GeneralSectionWrapper>

<GeneralSectionWrapper class="bg-primary-50 !py-0">
  <H1 class="pb-10 text-primary-700">{$_('matOgDrikke.ourRestaurants')}</H1>
  <LocationButtonMenu {activeId} {locations} />
</GeneralSectionWrapper>
{#if serviceAreaElementsByKey && elementCount > 0}
  <GeneralSectionWrapper class="bg-primary-50">
    <CardListWrapper class="pt-8">
      {#each serviceAreaElementKeys as key}
        {@const element = serviceAreaElementsByKey[key]}
        {#if element.location === activeId}
          <ResourceCard
            fitHeightToOuterDiv
            flexGrow={elementCount < 3 ? '' : 'flex-grow'}
            image={element.image}
            title={element.title}
            url={`${pageHrefs.spisested()}/${element.slug}`}
            max={false}
          />
        {/if}
      {/each}
    </CardListWrapper>
  </GeneralSectionWrapper>
{/if}

{#key ctaSectionDouble}
  <CtaDoubleSection {ctaSectionDouble} class="bg-base-white py-4 md:pb-8" />
{/key}
<CtaSection ctaSection={ctaSection1} />
<CtaSection ctaSection={ctaSection2} />
<CtaSection ctaSection={ctaSection2} />
<Resources {resources} pageHref={pageHrefs.matogdrikkeressurser()} />
<LandingPageFaqWrapper {relevantFaqs} />
